import { useLanguage } from '../../context/LanguageContext';
import React, { useState, useEffect } from 'react';
import { FiShield, FiSmartphone, FiMail, FiKey, FiMonitor, FiAlertTriangle, FiClock, FiTrash2, FiCheckCircle, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../../services/api';

const AccountSecurityPage = () => {
  const { t } = useLanguage();
  const [tab, setTab] = useState('mfa');
  const [mfaStatus, setMfaStatus] = useState(null);
  const [mfaSetup, setMfaSetup] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [disablePassword, setDisablePassword] = useState('');
  const [loginHistory, setLoginHistory] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (tab === 'mfa') fetchMfaStatus();
    if (tab === 'history') fetchLoginHistory();
    if (tab === 'sessions') fetchSessions();
    if (tab === 'alerts') fetchAlerts();
  }, [tab]);

  const fetchMfaStatus = async () => { try { const { data } = await api.get('/mfa/status'); setMfaStatus(data.data); } catch {} };
  const fetchLoginHistory = async () => { try { const { data } = await api.get('/security/login-history'); setLoginHistory(data.data || []); } catch {} };
  const fetchSessions = async () => { try { const { data } = await api.get('/security/active-sessions'); setSessions(data.data || []); } catch {} };
  const fetchAlerts = async () => { try { const { data } = await api.get('/security/alerts'); setAlerts(data.data || []); } catch {} };

  const setupMfa = async (method) => {
    setLoading(true);
    try {
      const { data } = await api.post('/mfa/setup', { method });
      setMfaSetup(data.data);
      toast.info(data.data.message);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    setLoading(false);
  };

  const verifyMfa = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/mfa/verify', { code: mfaCode });
      setBackupCodes(data.data.backupCodes);
      setMfaSetup(null);
      setMfaCode('');
      fetchMfaStatus();
      toast.success('MFA enabled!');
    } catch (err) { toast.error(err.response?.data?.message || 'Invalid code'); }
    setLoading(false);
  };

  const disableMfa = async () => {
    try {
      await api.post('/mfa/disable', { password: disablePassword });
      setDisablePassword('');
      fetchMfaStatus();
      toast.success('MFA disabled');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const revokeSession = async (sessionId) => {
    await api.delete(`/security/sessions/${sessionId}`);
    fetchSessions();
    toast.success('Session revoked');
  };

  const revokeAll = async () => {
    await api.delete('/security/sessions');
    fetchSessions();
    toast.success('All sessions revoked');
  };

  const tabs = [
    ['mfa', 'MFA Setup', FiShield],
    ['history', 'Login History', FiClock],
    ['sessions', 'Active Sessions', FiMonitor],
    ['alerts', 'Security Alerts', FiAlertTriangle],
  ];

  return (
    <div className="container py-4 fd-page-container" style={{ maxWidth: '900px' }}>
      <h3 style={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px' }}>
        <FiShield size={22} style={{ color: '#B91C1C' }} /> Account Security
      </h3>

      <div className="d-flex gap-2 my-3 flex-wrap">
        {tabs.map(([key, label, Icon]) => (
          <button key={key} className={`btn ${tab === key ? 'btn-primary' : 'btn-outline-secondary'} btn-sm`}
            onClick={() => setTab(key)} style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {/* MFA Tab */}
      {tab === 'mfa' && (
        <div className="dashboard-card p-4">
          <h5 style={{ fontWeight: 700 }}>{t('mfa')}</h5>
          {mfaStatus?.mfaEnabled ? (
            <div>
              <div className="d-flex align-items-center gap-2 mb-3">
                <FiCheckCircle size={20} style={{ color: '#16A34A' }} />
                <span style={{ fontWeight: 600, color: '#16A34A' }}>MFA is enabled via {mfaStatus.mfaMethod}</span>
              </div>
              <p className="text-muted">Enter your password to disable MFA:</p>
              <div className="d-flex gap-2" style={{ maxWidth: '350px' }}>
                <input type="password" className="form-control form-control-sm" placeholder="Password"
                  value={disablePassword} onChange={e => setDisablePassword(e.target.value)} />
                <button className="btn btn-outline-danger btn-sm" onClick={disableMfa} disabled={!disablePassword}>{t('disable')}</button>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-muted mb-3">Add an extra layer of security to your account.</p>
              {!mfaSetup ? (
                <div className="row g-3">
                  {[['email', 'Email OTP', FiMail, 'Receive a code via email each login'],
                    ['sms', 'SMS OTP', FiSmartphone, 'Receive a code via text message'],
                    ['authenticator', 'Authenticator App', FiKey, 'Use Google/Microsoft Authenticator']
                  ].map(([method, label, Icon, desc]) => (
                    <div key={method} className="col-md-4">
                      <div className="border rounded p-3 text-center h-100" style={{ cursor: 'pointer' }}
                        onClick={() => setupMfa(method)}>
                        <Icon size={28} style={{ color: '#B91C1C' }} />
                        <p className="mt-2 mb-1" style={{ fontWeight: 700, fontSize: '14px' }}>{label}</p>
                        <p className="mb-0 text-muted" style={{ fontSize: '12px' }}>{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ maxWidth: '400px' }}>
                  <p style={{ fontWeight: 600 }}>{mfaSetup.message}</p>
                  {mfaSetup.otpauthUrl && (
                    <div className="p-3 bg-light rounded mb-3 text-center">
                      <code style={{ fontSize: '11px', wordBreak: 'break-all' }}>{mfaSetup.otpauthUrl}</code>
                      <p className="mt-2 mb-0 text-muted" style={{ fontSize: '12px' }}>Copy this URI into your authenticator app</p>
                    </div>
                  )}
                  <div className="d-flex gap-2">
                    <input className="form-control" placeholder="Enter 6-digit code" maxLength={6}
                      value={mfaCode} onChange={e => setMfaCode(e.target.value)} style={{ fontFamily: 'monospace', fontWeight: 700, letterSpacing: '4px' }} />
                    <button className="btn btn-primary" onClick={verifyMfa} disabled={loading || mfaCode.length < 6}>{t('verify')}</button>
                  </div>
                  <button className="btn btn-link btn-sm mt-2 p-0" onClick={() => setMfaSetup(null)}>{t('cancel')}</button>
                </div>
              )}
            </div>
          )}

          {backupCodes && (
            <div className="mt-4 p-3 border border-warning rounded" style={{ background: '#FEF3C7' }}>
              <p style={{ fontWeight: 700, color: '#92400E' }}>⚠️ Save your backup codes! They won't be shown again.</p>
              <div className="d-flex flex-wrap gap-2">
                {backupCodes.map((code, i) => (
                  <code key={i} className="px-2 py-1 bg-white rounded" style={{ fontSize: '13px', fontWeight: 700 }}>{code}</code>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Login History Tab */}
      {tab === 'history' && (
        <div className="dashboard-card p-4">
          <h5 style={{ fontWeight: 700 }}>{t('loginHistory')}</h5>
          {loginHistory.length === 0 ? <p className="text-muted">{t('noLoginHistory')}</p> : (
            <div className="table-responsive">
              <table className="table table-sm" style={{ fontSize: '13px' }}>
                <thead><tr><th>{t('date')}</th><th>{t('device')}</th><th>{t('browserOs')}</th><th>{t('ip')}</th><th>{t('status')}</th></tr></thead>
                <tbody>
                  {loginHistory.map(l => (
                    <tr key={l._id}>
                      <td>{new Date(l.createdAt).toLocaleString()}</td>
                      <td><span className="badge bg-light text-dark">{l.device}</span></td>
                      <td>{l.browser} / {l.os}</td>
                      <td><code style={{ fontSize: '11px' }}>{l.ip}</code></td>
                      <td><span className={`badge bg-${l.status === 'success' ? 'success' : 'danger'}`}>{l.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Active Sessions Tab */}
      {tab === 'sessions' && (
        <div className="dashboard-card p-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 style={{ fontWeight: 700, margin: 0 }}>{t('activeSessions')}</h5>
            <button className="btn btn-outline-danger btn-sm" onClick={revokeAll}>{t('revokeAll')}</button>
          </div>
          {sessions.length === 0 ? <p className="text-muted">{t('noActiveSessions')}</p> :
            sessions.map(s => (
              <div key={s._id} className="d-flex justify-content-between align-items-center p-3 border-bottom">
                <div>
                  <div style={{ fontWeight: 600, fontSize: '14px' }}>
                    <FiMonitor size={14} className="me-1" /> {s.browser} on {s.os}
                  </div>
                  <div className="text-muted" style={{ fontSize: '12px' }}>
                    {s.device} · IP: {s.ip} · {new Date(s.createdAt).toLocaleString()}
                  </div>
                </div>
                <button className="btn btn-outline-danger btn-sm" onClick={() => revokeSession(s.sessionId)}>
                  <FiX size={14} />
                </button>
              </div>
            ))
          }
        </div>
      )}

      {/* Security Alerts Tab */}
      {tab === 'alerts' && (
        <div className="dashboard-card p-4">
          <h5 style={{ fontWeight: 700 }}>{t('securityAlerts')}</h5>
          {alerts.length === 0 ? <p className="text-muted">{t('noSecurityAlerts')}</p> :
            alerts.map(a => (
              <div key={a._id} className={`p-3 mb-2 rounded border-start border-3 ${!a.isRead ? 'bg-light' : ''}`}
                style={{ borderColor: a.severity === 'high' || a.severity === 'critical' ? '#EF4444' : a.severity === 'medium' ? '#F59E0B' : '#6B7280' }}>
                <div className="d-flex justify-content-between">
                  <span style={{ fontWeight: 700, fontSize: '14px' }}>{a.title}</span>
                  <span className={`badge bg-${a.severity === 'high' || a.severity === 'critical' ? 'danger' : a.severity === 'medium' ? 'warning' : 'secondary'}`}>
                    {a.severity}
                  </span>
                </div>
                <p className="mb-0 text-muted" style={{ fontSize: '13px' }}>{a.message}</p>
                <small className="text-muted">{new Date(a.createdAt).toLocaleString()}</small>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
};

export default AccountSecurityPage;
